import { Link } from "@tanstack/react-router";
import { useState } from "react";
import type { Adorn } from "@/components/persons/person-profile/use-pending-adornments";
import { DetailSection, MetadataRow } from "@/components/shared/detail-section";
import { OriginLink } from "@/components/shared/origin-link";
import type { PersonDetail } from "@/hooks/use-persons";
import { useRoleClassificationOptions } from "@/hooks/use-role-classifications";
import { originLinkSearch } from "@/lib/detail-origin";
import { PersonHistoryButton } from "../PersonHistoryDialog";

const LINK_CLASS =
  "font-medium text-primary underline decoration-primary/40 underline-offset-2 transition-colors hover:decoration-primary";

/**
 * A field that carries BOTH a scheduled-change marker and its own history
 * panel — manager, dotted line and role classification. Each owns its open
 * state here rather than at the top of the tab; nothing else needs to know.
 *
 * Only viewers allowed to see the history get the button. The route enforces
 * the same rule — this just avoids offering a control that would 403.
 */
function HistoryAdornment({
  person,
  attribute,
  marker,
  isAdmin,
  canViewHistory,
}: {
  person: PersonDetail;
  attribute: "manager" | "dotted_line" | "role_classification";
  marker: React.ReactNode;
  isAdmin: boolean;
  canViewHistory: boolean;
}) {
  const [open, setOpen] = useState(false);
  return (
    <span className="inline-flex items-center gap-1">
      {marker}
      {canViewHistory && (
        <PersonHistoryButton
          personId={person.id}
          personName={person.name}
          attribute={attribute}
          isAdmin={isAdmin}
          open={open}
          onOpenChange={setOpen}
        />
      )}
    </span>
  );
}

export function RoleOverviewCard({
  person,
  adorn,
  isAdmin,
  canViewHistory,
}: {
  person: PersonDetail;
  adorn: Adorn;
  isAdmin: boolean;
  canViewHistory: boolean;
}) {
  // Role classification is admin-managed config, so its label comes from that
  // table; `labelFor` falls back to the raw token for a retired value.
  const classifications = useRoleClassificationOptions();

  return (
    <DetailSection title="Role Overview" boldTitle className="gap-4 py-5">
      <div className="grid grid-cols-1 gap-x-8 gap-y-5 sm:grid-cols-2 lg:grid-cols-3">
        {person.departmentName ? (
          <MetadataRow label="Department" adornment={adorn("departmentId")}>
            <OriginLink to="/dashboard/organization" className={LINK_CLASS}>
              {person.departmentName}
            </OriginLink>
          </MetadataRow>
        ) : (
          <MetadataRow label="Department" value={null} adornment={adorn("departmentId")} />
        )}
        {person.roleTeamName ? (
          <MetadataRow label="Role Team" adornment={adorn("roleTeamId")}>
            <OriginLink to="/dashboard/organization" className={LINK_CLASS}>
              {person.roleTeamName}
            </OriginLink>
          </MetadataRow>
        ) : (
          <MetadataRow label="Role Team" value={null} adornment={adorn("roleTeamId")} />
        )}
        {/* Employee profile type (Builder / Connector / Craftsperson) — shown
            beside Role Classification, the other org-level categorisation. */}
        <MetadataRow
          label="Employee Profile"
          value={person.employeeProfileName}
          adornment={adorn("employeeProfileId")}
        />
        <MetadataRow
          label="Role Classification"
          adornment={
            // NOS models this history but has never filled it, so Enable's
            // panel starts empty and fills from the first change made here —
            // the dialog's blurb says so, rather than looking broken.
            <HistoryAdornment
              person={person}
              attribute="role_classification"
              marker={adorn("roleClassification")}
              isAdmin={isAdmin}
              canViewHistory={canViewHistory}
            />
          }
          value={
            person.roleClassification ? classifications.labelFor(person.roleClassification) : null
          }
        />
        <ManagerRow
          label="Manager"
          personId={person.managerId}
          personName={person.managerName}
          subject={person}
          attribute="manager"
          marker={adorn("managerId")}
          isAdmin={isAdmin}
          canViewHistory={canViewHistory}
        />
        {/* Dotted line (mentor) — links to the mentor's profile, mirroring the
            Manager row. */}
        <ManagerRow
          label="Dotted line"
          personId={person.dottedLineId}
          personName={person.dottedLineName}
          subject={person}
          attribute="dotted_line"
          marker={adorn("dottedLineId")}
          isAdmin={isAdmin}
          canViewHistory={canViewHistory}
        />
        {/* The person↔account link (person.userId) is intentionally NOT
            surfaced in the UI — it still drives self-service, the "You" badge,
            and history/phone visibility, but it's an internal link. */}
      </div>
    </DetailSection>
  );
}

/** Manager and Dotted line: the same row, differing only in which field. */
function ManagerRow({
  label,
  personId,
  personName,
  subject,
  attribute,
  marker,
  isAdmin,
  canViewHistory,
}: {
  label: string;
  personId: string | null;
  personName: string | null;
  subject: PersonDetail;
  attribute: "manager" | "dotted_line";
  marker: React.ReactNode;
  isAdmin: boolean;
  canViewHistory: boolean;
}) {
  const adornment = (
    <HistoryAdornment
      person={subject}
      attribute={attribute}
      marker={marker}
      isAdmin={isAdmin}
      canViewHistory={canViewHistory}
    />
  );
  if (!personId || !personName) {
    return <MetadataRow label={label} value={null} adornment={adornment} />;
  }
  return (
    <MetadataRow label={label} adornment={adornment}>
      <Link
        to="/dashboard/persons/$personId"
        params={{ personId }}
        search={originLinkSearch("person", subject.id, subject.name)}
        className={LINK_CLASS}
      >
        {personName}
      </Link>
    </MetadataRow>
  );
}
